import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAudioProcessor } from '../components/AudioRecorder/useAudioProcessor';
import { handleError } from './errors/errorUtils';

const AudioTelegramRecorderContext = createContext();

const isMediaRecorderSupported = () => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm');

export const AudioTelegramRecorderProvider = ({ children, telegramWebApp, userId }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [telegramWarning, setTelegramWarning] = useState(!!telegramWebApp);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isTelegram = !!telegramWebApp;
  const { processAudioComplete } = useAudioProcessor(0.01, 100);

  useEffect(() => {
    const supported = isMediaRecorderSupported();
    setIsSupported(supported);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkMicrophoneAccess = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (isMounted) {
          streamRef.current = stream;
          setPermissionGranted(true);
        } else {
          stream.getTracks().forEach(track => track.stop());
        }
      } catch (error) {
        if (isMounted) {
          setPermissionGranted(false);
          handleError({
            code: 'ERROR_MICROPHONE_ACCESS',
            error,
            context: {
              module: 'audio_telegram_recorder',
              message: 'Ошибка доступа к микрофону при инициализации',
              userId,
              isTelegram,
            },
            showToUser: false,
          });
        }
      }
    };

    if (isSupported && isTelegram) {
      checkMicrophoneAccess();
    }

    return () => {
      isMounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isSupported, userId, isTelegram]);

  const ensureMicrophoneAccess = useCallback(async () => {
    if (streamRef.current && streamRef.current.getAudioTracks().some(track => track.readyState === 'live')) {
      return streamRef.current;
    }

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaDevices API не поддерживается');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermissionGranted(true);
      return stream;
    } catch (error) {
      setPermissionGranted(false);
      handleError({
        code: 'ERROR_MICROPHONE_ACCESS',
        error,
        context: {
          module: 'audio_telegram_recorder',
          message: 'Ошибка доступа к микрофону',
          userId,
          isTelegram,
        },
        showToUser: false,
      });
      throw error;
    }
  }, [userId, isTelegram]);

  const startRecording = useCallback(
    async (onStartRecording, onStopRecording) => {
      if (!isSupported || isRecording || !permissionGranted) {
        handleError({
          code: 'ERROR_START_RECORDING',
          error: new Error('Нельзя начать запись'),
          context: {
            module: 'audio_telegram_recorder',
            message: 'Не удалось начать запись',
            userId,
            isTelegram,
            isSupported,
            isRecording,
            permissionGranted,
          },
          showToUser: false,
        });
        return;
      }

      try {
        const stream = await ensureMicrophoneAccess();
        chunksRef.current = [];
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = event => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          setIsRecording(false);
          const completeBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
          chunksRef.current = [];
          mediaRecorderRef.current = null;

          if (!completeBlob || completeBlob.size === 0) {
            onStopRecording(null, { error: 'Пустой Blob' });
            handleError({
              code: 'ERROR_EMPTY_BLOB',
              error: new Error('Пустой Blob'),
              context: {
                module: 'audio_telegram_recorder',
                message: 'Пустой Blob при остановке записи',
                userId,
                isTelegram,
              },
              showToUser: false,
            });
            return;
          }

          try {
            const { wavBlob, duration } = await processAudioComplete(completeBlob);
            onStopRecording(wavBlob, { duration });
          } catch (error) {
            handleError({
              code: 'ERROR_PROCESSING_AUDIO',
              error,
              context: {
                module: 'audio_telegram_recorder',
                message: 'Ошибка обработки аудио',
                userId,
                isTelegram,
              },
              showToUser: false,
            });
            onStopRecording(completeBlob, { error: 'Ошибка обработки аудио' });
          }
        };

        mediaRecorder.onerror = event => {
          handleError({
            code: 'ERROR_MEDIA_RECORDER',
            error: event.error,
            context: {
              module: 'audio_telegram_recorder',
              message: 'Ошибка MediaRecorder',
              userId,
              isTelegram,
            },
            showToUser: false,
          });
          setIsRecording(false);
          mediaRecorderRef.current = null;
          onStopRecording(null, { error: event.error?.message || 'Ошибка записи' });
        };

        if (mediaRecorder.state === 'inactive') {
          mediaRecorder.start();
          setIsRecording(true);
          setTelegramWarning(false);
          onStartRecording?.();
        } else {
          handleError({
            code: 'ERROR_INVALID_MEDIA_RECORDER_STATE',
            error: new Error('MediaRecorder не в состоянии inactive'),
            context: {
              module: 'audio_telegram_recorder',
              message: 'MediaRecorder не в состоянии inactive',
              userId,
              isTelegram,
              state: mediaRecorder.state,
            },
            showToUser: false,
          });
        }
      } catch (error) {
        handleError({
          code: 'ERROR_START_RECORDING',
          error,
          context: {
            module: 'audio_telegram_recorder',
            message: 'Ошибка начала записи',
            userId,
            isTelegram,
          },
          showToUser: false,
        });
        setIsRecording(false);
      }
    },
    [isSupported, isRecording, permissionGranted, userId, isTelegram, ensureMicrophoneAccess, processAudioComplete]
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    } else {
      handleError({
        code: 'ERROR_STOP_RECORDING',
        error: new Error('Запись не активна или MediaRecorder не в состоянии recording'),
        context: {
          module: 'audio_telegram_recorder',
          message: 'Невозможно остановить запись',
          userId,
          isTelegram,
          state: mediaRecorderRef.current?.state,
          isRecording,
        },
        showToUser: false,
      });
    }
  }, [isRecording, userId, isTelegram]);

  const value = {
    isRecording,
    startRecording,
    stopRecording,
    isSupported,
    permissionGranted,
    telegramWarning,
  };

  return (
    <AudioTelegramRecorderContext.Provider value={value}>
      {children}
    </AudioTelegramRecorderContext.Provider>
  );
};

export const useAudioTelegramRecorder = () => useContext(AudioTelegramRecorderContext);